// src/services/homeAdmin.js
//
// The admin part of the personal home page (/home, UI revamp step 3). Pure:
// the page loads the data with the loaders the admin pages already use and
// these turn it into what's shown. Each rule matches the page it links to:
//   - study counts: the Admin Dashboard's top numbers (same status keys)
//   - numbers to approve: the Dashboard's amber cards (studiesWithPendingNumbers)
//   - enrollments waiting: /admin/study-patients → Pending approval
//   - goal points: Admin Reports → Goal points to approve (buildGoalPointCards)
// Protocol versions are left out for now (admins upload them themselves).
//
// Snippets (user feedback, 2026-11-02), each with its page's own rule:
//   - credential alerts: the Notifications page (credentialStatus.js)
//   - today's visits: the Research Calendar, every coordinator
//   - latest submitted weekly reports: Admin Reports → Submitted reports

import { buildGoalPointCards } from "./goalRules";
import { goalFromDoc, goalPct, mergeLeaderboard } from "./goalProgress";
import { CREDENTIAL_FIELDS, checkStatus, needsAttention, statusSeverity } from "./credentialStatus";

/**
 * The CRC page's two Goal Progress circles for a month, with its own rules
 * (CoordinatorPage): totals over the leaderboard roster (active coordinators
 * not excluded from the leaderboard), the randomization goal defaulting to 12,
 * the screening goal "not set" when missing or 0.
 *   roster: the coordinators collection; enrollDocs / screenDocs: that month's
 *   per-coordinator counts ([{ name, count }]); *GoalDoc: { exists, data }.
 * -> { names, enrollments: { total, goal, pct }, screenings: { total, goal, pct } }
 */
export const goalSnapshot = ({ roster = [], enrollDocs = [], screenDocs = [], enrollGoalDoc, screenGoalDoc }) => {
  const names = (roster || [])
    .filter((c) => c && c.name && c.active !== false && c.includeInLeaderboard !== false)
    .map((c) => c.name);
  const eTotal = mergeLeaderboard(enrollDocs, names).total;
  const sTotal = mergeLeaderboard(screenDocs, names).total;
  const eGoal =
    enrollGoalDoc && enrollGoalDoc.exists ? Math.max(Number(enrollGoalDoc.data && enrollGoalDoc.data.goal) || 12, 0) : 12;
  const sGoal = goalFromDoc("screenings", !!(screenGoalDoc && screenGoalDoc.exists), screenGoalDoc && screenGoalDoc.data);
  return {
    names,
    enrollments: { total: eTotal, goal: eGoal, pct: goalPct(eTotal, eGoal) ?? 0 },
    screenings: { total: sTotal, goal: sGoal, pct: goalPct(sTotal, sGoal) },
  };
};

const statusKey = (s) => (s || "").toString().trim().toUpperCase();

/** The Admin Dashboard's study numbers: { total, active, startup, closed }. */
export const studyCounts = (studies = []) => ({
  total: studies.length,
  active: studies.filter((s) => statusKey(s.STATUS) === "ACTIVE").length,
  startup: studies.filter((s) => statusKey(s.STATUS) === "START-UP").length,
  closed: studies.filter((s) => statusKey(s.STATUS) === "CLOSED/CLOSING").length,
});

const studyName = (s) => s.NAME || s.STUDY_ID || "Study";

/** The studies with weekly-report numbers waiting (a Set of study doc ids), by name. */
export const studiesToApprove = (studies = [], ids) =>
  studies
    .filter((s) => ids && ids.has(s.id))
    .map((s) => ({ id: s.id, name: studyName(s) }))
    .sort((a, b) => String(a.name).localeCompare(String(b.name)));

/**
 * Goal points waiting on Admin Reports (from loadGoalPointData): the coordinators
 * with a card there (one card per coordinator × goal × month).
 */
export const goalPointCoordinators = (data) =>
  data ? [...new Set(buildGoalPointCards(data).map((c) => c.coordinator))].sort((a, b) => a.localeCompare(b)) : [];

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// Short words for the snippet; the Notifications page shows the full status.
const SHORT_STATUS = [
  ["Expired", "Expired"],
  ["Invalid Date", "Date not readable"],
  ["Expiring tomorrow", "Expiring tomorrow"],
  ["within 1 month", "Within 1 month"],
  ["within 6 months", "Within 6 months"],
];
const shortStatus = (status) => (SHORT_STATUS.find(([k]) => status.includes(k)) || [null, status])[1];

/**
 * Staff credentials the Notifications page would list (employeeCredentials
 * docs), one row per credential, most urgent first (then by name).
 * -> { rows: [{ key, employeeId, name, label, value, status, short, severity }], urgent, total }
 * `urgent` = expired / unreadable / expiring tomorrow (the page's red and orange).
 */
export const credentialAlerts = (employees = [], check = checkStatus) => {
  const rows = [];
  employees.forEach((e) => {
    CREDENTIAL_FIELDS.forEach(({ field, label }) => {
      const status = check(e[field]);
      if (!needsAttention(status)) return;
      rows.push({
        key: `${e.id}:${field}`,
        employeeId: e.id,
        name: e.NAME || "Unnamed",
        label,
        value: e[field] || "",
        status,
        short: shortStatus(status),
        severity: statusSeverity(status),
      });
    });
  });
  rows.sort((a, b) => b.severity - a.severity || String(a.name).localeCompare(String(b.name)));
  return { rows, urgent: rows.filter((r) => r.severity === 3).length, total: rows.length };
};

/** Research Calendar events starting today (local time), earliest first, every coordinator. */
export const todaysVisits = (events = [], now = new Date()) => {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 1);
  return (events || [])
    .map((e) => ({ ...e, startDate: new Date(e && e.start) }))
    .filter((e) => !Number.isNaN(e.startDate.getTime()) && e.startDate >= start && e.startDate < end)
    .sort((a, b) => a.startDate - b.startDate);
};

/** The newest submitted weekly reports (already newest first from the loader). */
export const latestReports = (reports = [], n = 5) => (reports || []).slice(0, n);

/**
 * The "Needs your attention" list. Each part is { loading, data, error } (one
 * per loader, so one failing doesn't hide the rest). Returns
 * { items: [{ id, text, to, state?, studies? }], pending, failed } where
 * `pending` = still loading and `failed` = the parts that couldn't load.
 */
export const attentionItems = ({ approvals, studies, requests, goalPoints }) => {
  const items = [];
  const failed = [];
  let pending = false;
  const part = (p, name) => {
    if (!p) return null;
    if (p.loading) {
      pending = true;
      return null;
    }
    if (p.error) {
      failed.push(name);
      return null;
    }
    return p.data;
  };

  const ids = part(approvals, "study numbers");
  const list = part(studies, "study numbers");
  if (ids && list) {
    const toApprove = studiesToApprove(list, ids);
    if (toApprove.length) {
      items.push({
        id: "numbers",
        text: `${plural(toApprove.length, "study has", "studies have")} weekly-report numbers to approve`,
        studies: toApprove,
      });
    }
  }

  const reqs = part(requests, "enrollment approvals");
  if (reqs && reqs.length) {
    items.push({
      id: "enrollments",
      text: `${plural(reqs.length, "enrollment", "enrollments")} waiting for your approval`,
      to: "/admin/study-patients",
      state: { tab: "pending" },
    });
  }

  const coords = part(goalPoints, "goal points");
  if (coords && coords.length) {
    items.push({
      id: "goal-points",
      text: `Goal points to approve for ${plural(coords.length, "coordinator", "coordinators")}`,
      to: "/admin/weekly-reports",
      state: { section: "goal-points" },
    });
  }

  return { items, pending, failed: [...new Set(failed)] };
};
